import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useUpdateNamedPrice } from '@/services/namedPrice/namedPrice.service';
import { NamedPrice } from '@/types/namedPrice.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { suppressApiTimeoutToast, enableApiTimeoutToast } from '@/apiClient/apiClient';
import styles from './EditBundleAddOnDrawer.module.css';

export interface EditBundleAddOnDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  namedPrice: NamedPrice | null;
  afterSave?: () => void;
}

function arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((v, i) => v === sortedB[i]);
}

const EditBundleAddOnDrawer = ({ isOpen, onClose, namedPrice, afterSave }: EditBundleAddOnDrawerProps) => {
  const { t } = useTranslation();
  const listItem = namedPrice;
  const updateMutation = useUpdateNamedPrice(listItem?.namedPriceId ?? '');

  const [isVisible, setIsVisible] = useState(true);
  const [isPriceLocked, setIsPriceLocked] = useState(false);
  const [prices, setPrices] = useState<number[]>([]);
  const [defaultPrice, setDefaultPrice] = useState<number | null>(null);
  const [newPriceInput, setNewPriceInput] = useState('');
  const [addPriceError, setAddPriceError] = useState('');

  const initialRef = useRef<{
    isVisible: boolean;
    priceLock: boolean;
    prices: number[];
    defaultPrice: number | null;
  } | null>(null);

  // Suppress timeout toast when drawer opens
  useEffect(() => {
    if (isOpen) {
      suppressApiTimeoutToast();
    } else {
      // Only re-enable after a small delay to ensure any ongoing requests have completed
      const timer = setTimeout(() => {
        enableApiTimeoutToast();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (listItem) {
      setAddPriceError('');
      const pr = listItem.availablePrices ?? [];
      const init = {
        isVisible: listItem.isVisible,
        priceLock: listItem.priceLock,
        prices: [...pr],
        defaultPrice: listItem.defaultPrice ?? (pr.length ? pr[0] : null),
      };
      initialRef.current = init;
      setIsVisible(init.isVisible);
      setIsPriceLocked(init.priceLock);
      setPrices(init.prices);
      setDefaultPrice(init.defaultPrice);
    }
  }, [listItem]);

  const initial = initialRef.current;

  const isDirty = useMemo(() => {
    if (!initial || !listItem) return false;
    return (
      isVisible !== initial.isVisible ||
      isPriceLocked !== initial.priceLock ||
      !arraysEqual(prices, initial.prices) ||
      defaultPrice !== initial.defaultPrice
    );
  }, [initial, listItem, isVisible, isPriceLocked, prices, defaultPrice]);

  const defaultPriceError = useMemo(() => {
    if (prices.length === 0) return 'noPrices';
    if (defaultPrice === null) {
      const initialDefault = listItem?.defaultPrice ?? initial?.defaultPrice ?? null;
      if (initialDefault != null && !prices.includes(initialDefault)) return 'removed';
      return 'notSelected';
    }
    if (!prices.includes(defaultPrice)) return 'removed';
    return null;
  }, [prices, defaultPrice, listItem?.defaultPrice, initial?.defaultPrice]);

  const saveDisabled = !isDirty || !!defaultPriceError || updateMutation.isPending;

  const displayName = listItem?.name ?? '-';
  const affectedBundles = useMemo(() => {
    const codes = listItem?.effectedBundles;
    if (Array.isArray(codes) && codes.length > 0) return codes.join(', ');
    return '-';
  }, [listItem]);

  const defaultPriceOptions = useMemo(() => {
    const withDefault = new Set<number>(prices.map((p) => Math.round(p * 100) / 100));
    if (defaultPrice != null) withDefault.add(Math.round(defaultPrice * 100) / 100);
    const sorted = [...withDefault].sort((a, b) => a - b);
    return [
      { value: '', label: t('common.select') },
      ...sorted.map((p) => {
        const key = p.toFixed(2);
        return { value: key, label: `$${key}` };
      }),
    ];
  }, [prices, defaultPrice, t]);

  const handleAddPriceInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAddPriceError('');
    const raw = e.target.value;
    const numericOnly = raw.replace(/[^0-9.]/g, '');
    setNewPriceInput(numericOnly);
  }, []);

  const handleAddPrice = useCallback(() => {
    setAddPriceError('');
    const val = parseFloat(newPriceInput);
    if (Number.isNaN(val) || newPriceInput.trim() === '') {
      setAddPriceError('Enter a valid price');
      return;
    }
    if (prices.includes(val)) {
      setAddPriceError('Price already exists');
      return;
    }
    const updated = [...prices, val].sort((a, b) => a - b);
    setPrices(updated);
    setNewPriceInput('');
  }, [newPriceInput, prices]);

  const handleRemovePrice = useCallback(
    (price: number) => {
      setAddPriceError('');
      const updated = prices.filter((p) => p !== price);
      setPrices(updated);
      if (defaultPrice === price) setDefaultPrice(null);
    },
    [prices, defaultPrice],
  );

  const handleSave = useCallback(() => {
    if (!listItem?.namedPriceId || saveDisabled) return;
    if (defaultPrice === null && prices.length > 0) return;
    const payload = {
      defaultPrice: defaultPrice ?? prices[0] ?? 0,
      availablePrices: prices,
      isVisible,
      priceLock: isPriceLocked,
    };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        showToast({
          id: 'editBundleAddOnSuccess',
          label: t('editBundleAddOn.saveSuccess'),
          toastType: ToastTypes.Success,
        });
        afterSave?.();
        onClose();
      },
      onError: () => {
        showToast({
          id: 'editBundleAddOnError',
          label: t('editBundleAddOn.saveError'),
          toastType: ToastTypes.Error,
        });
      },
    });
  }, [
    listItem?.namedPriceId,
    saveDisabled,
    isVisible,
    isPriceLocked,
    prices,
    defaultPrice,
    updateMutation,
    t,
    afterSave,
    onClose,
  ]);

  if (!listItem) return null;

  return (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        className: styles.drawerPaper,
        'data-testid': 'edit-bundle-add-on-drawer',
      }}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>{t('editBundleAddOn.title')}</h1>
          <div className={styles.buttonsRow}>
            <Button variant={ButtonVariants.Secondary} onClick={onClose} data-testid="edit-bundle-add-on-cancel">
              {t('editBundleAddOn.cancel')}
            </Button>
            <Button
              variant={ButtonVariants.Primary}
              onClick={handleSave}
              disabled={saveDisabled}
              data-testid="edit-bundle-add-on-save"
            >
              {t('editBundleAddOn.save')}
            </Button>
          </div>
        </header>

        <div className={styles.content}>
          <div className={styles.formRow}>
            <span className={styles.label}>{t('editBundleAddOn.namedPriceLabel')}</span>
            <span className={styles.value}>{displayName}</span>
          </div>

          <div className={styles.formRow}>
            <span className={styles.label}>{t('editBundleAddOn.bundlesAffectedLabel')}</span>
            <span className={styles.value}>{affectedBundles}</span>
          </div>

          <div className={styles.toggleRow}>
            <span className={styles.label}>{t('editBundleAddOn.visibilityLabel')}</span>
            <Switch color="error" checked={isVisible} onChange={(_, checked) => setIsVisible(checked)} />
            <span className={styles.toggleValue}>{isVisible ? 'ON' : 'OFF'}</span>
          </div>

          <div className={styles.toggleRow}>
            <span className={styles.label}>{t('editBundleAddOn.priceLockLabel')}</span>
            <Switch color="error" checked={isPriceLocked} onChange={(_, checked) => setIsPriceLocked(checked)} />
            <span className={styles.toggleValue}>{isPriceLocked ? 'ON' : 'OFF'}</span>
          </div>

          <div className={styles.formRow}>
            <span className={styles.label}>{t('editBundleAddOn.pricesLabel')}</span>
            <div className={styles.pricesContent}>
              <div className={styles.pricesRow}>
                {prices.map((p) => (
                  <span key={p} className={styles.priceChip}>
                    ${p.toFixed(2)}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemovePrice(p);
                      }}
                      aria-label={`Remove ${p}`}
                      className={styles.priceChipRemove}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className={styles.addPriceRow}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={styles.addPriceInput}
                  placeholder={t('editBundleAddOn.addNewPrice')}
                  value={newPriceInput}
                  onChange={handleAddPriceInputChange}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPrice())}
                />
                <Button variant={ButtonVariants.Primary} onClick={handleAddPrice}>
                  {t('editBundleAddOn.addButton')}
                </Button>
              </div>
              {addPriceError && <span className={styles.errorText}>{addPriceError}</span>}
            </div>
          </div>

          <div className={styles.formRow}>
            <span className={styles.label}>{t('editBundleAddOn.defaultPriceLabel')}</span>
            <div className={styles.defaultPriceWrap}>
              <select
                className={styles.defaultPriceSelect + (defaultPriceError ? ` ${styles.error}` : '')}
                value={defaultPrice != null ? (Math.round(defaultPrice * 100) / 100).toFixed(2) : ''}
                onChange={(e) => setDefaultPrice(e.target.value ? parseFloat(e.target.value) : null)}
              >
                {defaultPriceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {(defaultPriceError === 'removed' || defaultPriceError === 'notSelected') && (
                <span className={styles.errorText}>{t('editBundleAddOn.defaultPriceErrorRemoved')}</span>
              )}
              {defaultPriceError === 'noPrices' && (
                <span className={styles.errorText}>{t('editBundleAddOn.defaultPriceErrorNoPrices')}</span>
              )}
              <div className={styles.noteMessage}>
                <strong>Note:</strong> Changing the Default Price will impact all stores that do not have a price set
                for this Add-On.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default EditBundleAddOnDrawer;
